import React from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { EmptyState } from '@/components/EmptyState';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { SurveyCard } from '@/pages/Mail/components/SurveyCard';
import type { Survey } from '@/types/survey';

const FADE_RANGE_PX = 24;

interface Props {
  surveys: Survey[];
  isMobile: boolean;
  onOpen: (id: string) => void;
  onFill: (id: string) => void;
}

export const SurveyList: React.FC<Props> = ({
  surveys,
  isMobile,
  onOpen,
  onFill,
}) => {
  const { t } = useTranslation();
  const { progress, handleScroll } = useScrollFadeProgress(FADE_RANGE_PX);

  const isEmpty = surveys.length === 0;

  const cards = surveys.map((survey) => (
    <SurveyCard
      key={survey.id}
      survey={survey}
      isMobile={isMobile}
      onOpen={onOpen}
      onFill={onFill}
    />
  ));

  const fadeOverlay = (
    <div
      style={{ opacity: progress }}
      className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 bg-gradient-to-b from-constant-black/6 to-transparent transition-opacity duration-300 ease-out"
    />
  );

  const emptyState = (
    <div className="flex flex-1 flex-col pb-8">
      <EmptyState description={t('mail.surveys.empty')} />
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-card max-md:rounded-t-2xl max-md:shadow-card md:rounded-[20px]">
      {!isMobile && !isEmpty && (
        <p className="text-subhead-14 border-b border-divider px-5 pt-5 pb-4 text-muted-foreground">
          {t('mail.surveys.title')}
        </p>
      )}

      {isEmpty && emptyState}
      {!isEmpty && (
        <div className="relative flex min-h-0 flex-1 flex-col">
          {fadeOverlay}
          {isMobile && (
            <div
              onScroll={handleScroll}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto pt-4 pb-4"
            >
              <p className="px-4 pb-3 text-[18px] leading-6 font-semibold text-foreground">
                {t('mail.surveys.title')}
              </p>
              <div className="flex flex-col gap-3 px-4">{cards}</div>
            </div>
          )}
          {!isMobile && (
            <AutoHideScrollArea
              onScroll={handleScroll}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="flex flex-col gap-3 px-5 pt-3 pb-5">{cards}</div>
            </AutoHideScrollArea>
          )}
        </div>
      )}
    </div>
  );
};
